import { useEffect, useLayoutEffect, useRef, type KeyboardEvent, type ReactNode, type RefObject } from 'react';
import { lingerOnUnmount, replaceLeaving, resumeEntry } from '@/lib/exit-motion';
import { openerCandidates } from '@/lib/opener';
import { cn } from '@/lib/utils';

/**
 * The frame every confirm dialog in the product sits in.
 *
 * A modal is a decision laid over a page that is still there, and that is exactly what makes it dangerous
 * with a keyboard: focus left outside it walks an invisible document, and focus that can Tab out of it
 * silently operates controls the reader can no longer see. So this frame does three things and holds them
 * together in one place rather than four: it moves focus into the dialog when it opens, keeps Tab inside
 * while it is open, and hands focus back to the control that opened it when it closes.
 *
 * Escape closes it, because a question a keyboard cannot dismiss is a trap — and it closes it the same way
 * the Hủy button does, through the caller's own `onClose`, so nothing can be confirmed by walking away.
 * Nothing here decides *what* the dialog says or what confirming means: that stays with the screen.
 *
 * It is this product's only modal. Radix Dialog/AlertDialog are deliberately not ported: they lock scrolling
 * by writing a `<style>` element at runtime, which production's `style-src 'self'` refuses. `variant="sheet"`
 * is the same frame docked to the right edge at full height (the theme drawer uses it). `variant="form"` is a
 * compact form dialog, about 480 px wide; on a phone (≤560 px) it is a full-width sheet pinned to the bottom that
 * scrolls inside itself and rides above the on-screen keyboard (see `useKeyboardInset`). `variant="document"` is
 * the card detail: a large centred frame (`min(1280px, 100vw − 32px)` × `100dvh − 32px`) whose layout belongs to the
 * caller's stylesheet, full screen on a phone. `variant="compose"` is "Việc mới" (2026-09-25): `min(680px, 92vw)` wide,
 * top-aligned at 12vh like Linear's "New issue", its height its content's; full screen on a phone.
 */

/** Everything that can take the focus from Tab in some browser; `reachable` then drops what cannot right now. */
const FOCUSABLE = [
  'a[href]', 'area[href]', 'button', 'input:not([type="hidden"])', 'select', 'textarea', 'iframe',
  'audio[controls]', 'video[controls]', '[contenteditable]:not([contenteditable="false"])',
  'details > summary:first-of-type', '[tabindex]',
].join(',');

/**
 * Whether Tab can land on `el` as the panel stands now: not disabled, not taken out with `tabindex="-1"`, not
 * under `inert` or `aria-hidden`, not inside a closed `<details>` (its summary aside), and rendered — not
 * `display: none` (which is what `hidden` is) or `visibility: hidden`. The attribute walk holds in any engine;
 * the rendering question goes to `checkVisibility()` where the engine has it, and to the computed styles where
 * it does not (jsdom, which has no layout, and engines older than the method).
 */
function reachable(el: HTMLElement, panel: HTMLElement): boolean {
  if (el.tabIndex < 0 || el.matches(':disabled')) return false;
  for (let node: HTMLElement | null = el; node !== null && node !== panel; node = node.parentElement) {
    if (node.hasAttribute('inert') || node.getAttribute('aria-hidden') === 'true') return false;
    const parent: HTMLElement | null = node.parentElement;
    if (parent instanceof HTMLDetailsElement && !parent.open && node !== parent.querySelector(':scope > summary')) {
      return false;
    }
  }
  if (typeof el.checkVisibility === 'function') {
    return el.checkVisibility({ visibilityProperty: true, checkVisibilityCSS: true });
  }
  for (let node: HTMLElement | null = el; node !== null && node !== panel; node = node.parentElement) {
    if (getComputedStyle(node).display === 'none') return false;
  }
  return getComputedStyle(el).visibility !== 'hidden';
}

const focusableWithin = (panel: HTMLElement | null): HTMLElement[] => (panel === null
  ? []
  : [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => reachable(el, panel)));

const isNamedRadio = (el: Element | null): el is HTMLInputElement =>
  el instanceof HTMLInputElement && el.type === 'radio' && el.name !== '';

/**
 * The panel's controls as Tab actually visits them, from `from`, going forward or back.
 *
 * Every control is one stop except a radio group (same name, same form), which is one stop however many
 * radios it holds, because the browser leaves the group on the next Tab rather than walking its radios. The
 * stop is the radio that has the focus, if one does; otherwise the checked one; and if none is checked, the
 * first radio for a forward Tab and the last for a backward one. Counting every radio as a stop put the
 * "last control" on a radio Tab never rests on, so Tab from the checked swatch left the theme drawer.
 */
function tabStops(panel: HTMLElement | null, from: Element | null, forward: boolean): HTMLElement[] {
  const focusable = focusableWithin(panel);
  return focusable.filter((el) => {
    if (!isNamedRadio(el)) return true;
    const group = focusable.filter((other): other is HTMLInputElement =>
      isNamedRadio(other) && other.name === el.name && other.form === el.form);
    if (isNamedRadio(from) && group.includes(from)) return el === from;
    const checked = group.find((radio) => radio.checked);
    if (checked !== undefined) return el === checked;
    return el === (forward ? group[0] : group[group.length - 1]);
  });
}

/**
 * The stop Tab (or Shift+Tab) moves to from `from`, wrapping at either end. From a stop, it is the next one in
 * the list. From anything else — the panel itself, a message focused by hand, or the page when the control
 * that had the focus was removed — it is the first stop after that point in document order, going forward, or
 * the last one before it, going back; the panel holds all its stops, so from the panel that is the first stop
 * and the last.
 */
function nextStop(stops: HTMLElement[], from: Element | null, forward: boolean): HTMLElement | undefined {
  const at = stops.findIndex((stop) => stop === from);
  if (at >= 0) return stops[(at + (forward ? 1 : stops.length - 1)) % stops.length];
  if (from === null) return forward ? stops[0] : stops[stops.length - 1];
  const after = (stop: HTMLElement) => (from.compareDocumentPosition(stop) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0;
  return forward
    ? stops.find(after) ?? stops[0]
    : stops.findLast((stop) => !after(stop)) ?? stops[stops.length - 1];
}

/**
 * Inputs whose own parts take Tab: every engine measured (Chromium, WebKit) walks a date's day, month and year
 * with it before leaving the field, and a Shift+Tab into one enters at its last part. The panel cannot see
 * which part holds the caret, nor place it, so a Tab from or into one is left to the browser and checked
 * straight after (see the keydown handler).
 */
const SEGMENTED = new Set(['date', 'datetime-local', 'month', 'time', 'week']);
const isSegmented = (el: Element | null | undefined): boolean =>
  el instanceof HTMLInputElement && SEGMENTED.has(el.type);

/**
 * The dialog on top when one is opened from inside another — the unit editor sheet asks its own confirm. No
 * dialog is portaled, so the inner one sits inside the outer one's panel, and the last one there is the one
 * drawn over the rest. Only that one answers the keyboard: every open dialog listens for Tab on the document,
 * and the outer one's listener runs first, so an outer dialog that did not step aside moved the focus back
 * into itself, under the confirm, where one more Escape took the sheet and its unsaved edits with it.
 */
const OPEN_DIALOG = '[role="dialog"][aria-modal="true"]';
function topmost(panel: HTMLElement): HTMLElement {
  const inner = panel.querySelectorAll<HTMLElement>(OPEN_DIALOG);
  return inner.length === 0 ? panel : inner[inner.length - 1]!;
}

/**
 * How much of the layout viewport the on-screen keyboard covers, written to `--kb` on `el`. A phone keyboard
 * shrinks only the visual viewport, and a `fixed` layer is laid out against the layout one, so a sheet pinned to the
 * bottom would sit under the keyboard; its padding is lifted by this much instead. Set through the CSSOM, which
 * `style-src 'self'` allows.
 */
function useKeyboardInset(el: RefObject<HTMLElement | null>, on: boolean) {
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!on || !viewport) return undefined;
    const fit = () => {
      const covered = Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop);
      el.current?.style.setProperty('--kb', `${Math.round(covered)}px`);
    };
    fit();
    viewport.addEventListener('resize', fit);
    viewport.addEventListener('scroll', fit);
    return () => {
      viewport.removeEventListener('resize', fit);
      viewport.removeEventListener('scroll', fit);
    };
  }, [el, on]);
}

/** Each variant's motion (styles/motion.css), written to the scrim as `data-motion`. */
const MOTION = { dialog: 'pop', sheet: 'side', form: 'form', document: 'doc', compose: 'compose' } as const;

/** How each open panel is dismissed, so an Escape that reaches an outer dialog can close the one on top. */
const dismissals = new WeakMap<HTMLElement, () => void>();

export function ModalDialog({
  labelId, onClose, children, variant = 'dialog', className, initialFocus, motion, returnFocus,
}: {
  /** The id of the heading inside `children` that names this dialog. */
  labelId: string;
  /** Dismissal — Escape and the caller's own cancel control must mean the same thing. */
  onClose: () => void;
  children: ReactNode;
  /**
   * `dialog` (default) is centred near the top; `sheet` is docked to the right edge at full height; `form` is a
   * compact 480 px dialog that becomes a bottom sheet on a phone; `document` is the card modal and `compose` the
   * "Việc mới" frame, both full screen on a phone.
   */
  variant?: 'dialog' | 'sheet' | 'form' | 'document' | 'compose';
  /** Merged onto the panel last, so a caller can change its width or padding (the theme drawer does). */
  className?: string;
  /**
   * Where the focus goes on open: `'panel'` is the dialog itself (the card modal after a click, so a first key
   * press is J/K and not typing), a ref is that element. Left out, it is the first control, as for a question.
   */
  initialFocus?: 'panel' | RefObject<HTMLElement | null>;
  /**
   * How it comes and goes (styles/motion.css). Left out, it follows the variant; `'drop'` is the top-aligned
   * entry of "Việc mới" for a centred dialog that sits high (the ⌘K palette).
   */
  motion?: 'drop';
  /**
   * Where the focus goes back on close. Left out, it is the control a press just landed on (WebKit does not focus a
   * tapped button; see lib/opener.ts), else the one that held the focus when the dialog opened. A caller still
   * passes its opener when neither is it; this one wins.
   */
  returnFocus?: RefObject<HTMLElement | null>;
}) {
  const panel = useRef<HTMLElement>(null);
  const backdrop = useRef<HTMLDivElement>(null);
  useKeyboardInset(backdrop, variant === 'form' || variant === 'document' || variant === 'compose' || variant === 'sheet');
  const firstFocus = useRef(initialFocus);
  const kind = motion ?? MOTION[variant];

  /*
   * The way out (lib/exit-motion.ts). The caller unmounts this dialog, so its nodes stay in the page after React
   * lets go of them, inert and no longer modal, until the scrim's exit animation ends. Everything else here —
   * the focus handed back, the key listeners removed — happens at once, as before: nobody waits on the picture.
   * A reopen while it is still leaving takes it away and starts this entry from where that exit had got to.
   */
  useLayoutEffect(() => {
    const scrim = backdrop.current;
    if (scrim === null) return undefined;
    resumeEntry([scrim, panel.current], replaceLeaving(scrim, kind));
    return () => lingerOnUnmount(scrim, kind);
    // The motion is the dialog's kind, fixed for its life.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Captured before the first focus move, so it is the control that opened the dialog and not the dialog. The
    // caller's own `returnFocus` first; then the control a press just landed on, since WebKit focuses no tapped
    // button (lib/opener.ts); then what holds the focus; each with a menu item's trigger behind it.
    const explicit = returnFocus?.current ?? null;
    const openers = explicit === null ? openerCandidates() : [explicit, ...openerCandidates()];
    const chosen = firstFocus.current;
    const target = chosen === 'panel'
      ? panel.current
      : chosen?.current ?? tabStops(panel.current, null, true)[0] ?? panel.current;
    target?.focus();
    return () => {
      // A control removed while the dialog was open cannot take the focus back: the next one still in the page
      // does (a menu item's trigger). Nor can one still there that refuses it (disabled, hidden): the next one is
      // tried until one holds it. With none, the browser keeps the focus where it is rather than jumping to the
      // top, and the screen that removed the row says what happened.
      for (const el of openers) {
        if (!el.isConnected) continue;
        el.focus();
        if (document.activeElement === el) break;
      }
    };
    // The opener is the one of the dialog's opening, fixed for its life.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * Every Tab is moved by hand, not only the one at either end. The browser's own Tab order is not the
   * dialog's: WebKit's default skips buttons, links and radios, so from a middle stop it jumped past the last
   * stop and out to the page, where a check at the edges never saw it. On the document rather than the panel,
   * so a Tab pressed while the focus is on nothing (the control that held it was removed) still comes back in;
   * and it steps aside for a control inside that has already taken the key (`defaultPrevented`). Option+Tab is
   * Safari's own "every control" Tab, so it is handled too; Ctrl/⌘+Tab belong to the browser.
   */
  useEffect(() => {
    // The check owed after a Tab left to the browser (below). It runs on a timer, or at the next key press if
    // that comes first — a check that ran after the next Tab had moved the focus on would drag it back.
    let owed: (() => void) | null = null;
    let timer: number | undefined;
    const settle = () => {
      window.clearTimeout(timer);
      const check = owed;
      owed = null;
      check?.();
    };
    function onTab(event: globalThis.KeyboardEvent) {
      settle();
      if (event.key !== 'Tab' || event.ctrlKey || event.metaKey || event.defaultPrevented) return;
      const current = panel.current;
      if (current === null) return;
      // A dialog opened inside this one owns Tab; its own listener moves the focus, from wherever it is.
      if (topmost(current) !== current) return;
      const from = document.activeElement;
      const forward = !event.shiftKey;
      const stops = tabStops(current, from, forward);
      const to = nextStop(stops, from, forward);
      if (to === undefined) {
        event.preventDefault();
        current.focus();
        return;
      }
      const within = isSegmented(from) && stops.includes(from as HTMLElement);
      if (within || isSegmented(to)) {
        // The browser may only be moving between the field's parts, or entering one at the part it should.
        // Once it has acted: on the stop this dialog picked, or still in the field it left from, is its
        // business; anywhere else — a control WebKit chose, the page, nowhere — is corrected to that stop.
        owed = () => {
          const now = document.activeElement;
          if (!current.isConnected || now === to || (within && now === from)) return;
          to.focus();
        };
        timer = window.setTimeout(settle, 0);
        return;
      }
      event.preventDefault();
      to.focus();
    }
    document.addEventListener('keydown', onTab);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('keydown', onTab);
    };
  }, []);

  // The latest `onClose`, for an outer dialog's Escape to reach (see `onKeyDown`).
  const close = useRef(onClose);
  useEffect(() => { close.current = onClose; });
  useEffect(() => {
    const current = panel.current;
    if (current === null) return undefined;
    dismissals.set(current, () => close.current());
    return () => { dismissals.delete(current); };
  }, []);

  /*
   * Escape with the focus on the page rather than in any open dialog (a press that landed on nothing, a control
   * removed under the focus): the backdrop's `onKeyDown` below never hears it, so the document does. Only the
   * dialog on top of every open one answers, through the same `dismissals`, and an Escape something else already
   * took (`defaultPrevented`) is left alone.
   */
  useEffect(() => {
    function onEscape(event: globalThis.KeyboardEvent) {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      const current = panel.current;
      if (current === null) return;
      const active = document.activeElement;
      if (active instanceof Element && active.closest(OPEN_DIALOG) !== null) return;
      const open = document.querySelectorAll<HTMLElement>(OPEN_DIALOG);
      const top = open[open.length - 1];
      if (top !== current) return;
      event.preventDefault();
      dismissals.get(top)?.();
    }
    document.addEventListener('keydown', onEscape);
    return () => document.removeEventListener('keydown', onEscape);
  }, []);

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape') {
      // Stopped here: the navigation band closes its own menu on Escape, and one key press is one dismissal.
      event.stopPropagation();
      // A field inside already answered this key (a changed title reverted); the next Escape is the dialog's.
      if (event.defaultPrevented) return;
      // An Escape pressed inside a dialog opened in this one was stopped there. One that still reaches this
      // dialog while another is open inside it came from under that one, and it closes that one, not this.
      const top = panel.current === null ? null : topmost(panel.current);
      if (top !== null && top !== panel.current) {
        dismissals.get(top)?.();
        return;
      }
      onClose();
    }
  }

  const sheet = variant === 'sheet';
  const form = variant === 'form';
  const document_ = variant === 'document';
  const compose = variant === 'compose';
  // The prototype's dialog (h-prototype `components/ui/dialog.tsx`): a black 50 % scrim that blurs the page, a
  // bordered `max-w-lg` panel at `p-6`, a `text-lg` title set tight, and the description under it in the muted
  // body size 6 px below (DialogHeader's `space-y-1.5`) — the negative margin collapses into the title's 1 rem.
  // z-[60]: above the sticky header (z-30), the mobile navigation drawer (z-40) and its scrim.
  return (
    <div
      ref={backdrop}
      className={cn(
        'dialog-backdrop fixed inset-0 z-[60] flex overflow-y-auto bg-black/50 backdrop-blur-sm',
        sheet ? 'justify-end' : document_ ? 'items-center justify-center p-4' : 'items-start justify-center p-4',
        // A phone's form sheet: flush to the bottom, lifted above the keyboard.
        form && 'max-[560px]:items-end max-[560px]:p-0 max-[560px]:pb-[var(--kb,0px)]',
        // A phone's document or compose frame is the whole screen, over a scrim it covers anyway.
        (document_ || compose) && 'max-[560px]:items-start max-[560px]:p-0 max-[560px]:backdrop-blur-none',
      )}
      data-state="open"
      data-motion={kind}
      onKeyDown={onKeyDown}
      // A press on the scrim keeps the focus where it was in the dialog, so the keyboard still reaches it. It does
      // not close the dialog: a stray click must not throw away what a form or a card holds.
      onMouseDown={(event) => { if (event.target === event.currentTarget) event.preventDefault(); }}
    >
      <section
        className={cn(
          'dialog relative w-full border bg-white text-[hsl(var(--h-card-foreground))] shadow-lg outline-none',
          '[&>h2]:mb-4 [&>h2]:text-lg [&>h2]:font-semibold [&>h2]:leading-none [&>h2]:tracking-tight',
          '[&>h2+p]:-mt-2.5 [&>h2+p]:text-sm [&>h2+p]:text-[hsl(var(--h-muted-foreground))]',
          sheet
            ? 'min-h-dvh max-w-sm border-y-0 border-r-0 p-6'
            : form
              // 480 px and a 20 px inset on a desktop. On a phone the sheet scrolls inside itself, so the form's
              // pinned save row stays on screen above the keyboard while the fields scroll under the title.
              ? cn('my-[clamp(16px,8vh,80px)] max-w-[480px] rounded-lg p-5 [&>h2]:mb-3',
                'max-[560px]:my-0 max-[560px]:max-w-none max-[560px]:rounded-b-none max-[560px]:rounded-t-2xl',
                'max-[560px]:border-x-0 max-[560px]:border-b-0 max-[560px]:px-4 max-[560px]:pb-0 max-[560px]:pt-4',
                'max-[560px]:max-h-[calc(100dvh-var(--kb,0px)-16px)] max-[560px]:overflow-y-auto')
              : document_
                // The frame only; the caller's stylesheet lays out the header, the columns and the phone's bar.
                // The browser's height less a 16 px gap above and below (hotfix 2026-09-26), and wide up to 1280 px.
                ? cn('grid h-[calc(100dvh-32px)] w-[min(1280px,calc(100vw-32px))] max-w-none grid-rows-[auto_minmax(0,1fr)]',
                  'overflow-hidden rounded-xl p-0 shadow-[var(--h-shadow-lg)]',
                  'max-[560px]:h-[calc(100dvh-var(--kb,0px))] max-[560px]:w-full max-[560px]:rounded-none',
                  'max-[560px]:border-0 max-[560px]:grid-rows-[auto_minmax(0,1fr)_auto]')
                : compose
                  // Top-aligned at 12vh, the height its content's; the caller's stylesheet lays out the inside.
                  ? cn('mt-[clamp(8px,calc(12vh-16px),104px)] mb-4 w-[min(680px,92vw)] max-w-none overflow-hidden',
                    'rounded-xl p-0 shadow-[var(--h-shadow-lg)]',
                    'max-[560px]:m-0 max-[560px]:h-[calc(100dvh-var(--kb,0px))] max-[560px]:w-full',
                    'max-[560px]:rounded-none max-[560px]:border-0')
                  : 'my-[clamp(16px,8vh,80px)] max-w-lg rounded-lg p-6',
          className,
        )}
        data-variant={variant}
        role="dialog" aria-modal="true" aria-labelledby={labelId}
        ref={panel} tabIndex={-1}
      >
        {children}
      </section>
    </div>
  );
}
